import {
  LiveVisitAbdominalAuscultationSection,
  LiveVisitHeartAusculationSection,
  LiveVisitLungsAsculationSection,
} from '@higo/common/lib/features/ExaminationPage/sections';
import { AudioTrackGroupProvider } from '@higo/ui/lib/components/AudioPlayer';

import { VisitExaminationPageModel } from '@higo/common/lib/features/ExaminationPage/models';

import { useExaminationDetailsQuery } from '@higo/common/lib/features/ExaminationPage/hooks/queries';
import { LungsFrontAuscultationLegend } from '@higo/common/lib/components/Auscultation/LungsAuscultationLegend/LungsFrontAuscultationLegend';

import { LungsBackAuscultationLegend } from '@higo/common/lib/components/Auscultation/LungsAuscultationLegend/LungsBackAuscultationLegend';
import { HeartAuscultationLegend } from '@higo/common/lib/components/Auscultation/HeartAuscultationLegend/HeartAuscultationLegend';

import { PhysicalExaminationType } from '@higo/api';
import { format } from 'date-fns';
import { ExaminationPageLayout } from 'pages/ExaminationPage/ExaminationPageLayout';
import { FormattedMessage } from 'react-intl';
import { ExaminationPageSection } from '@higo/common/lib/features/ExaminationPage/sections/ExaminationPageSection';
import styled from 'styled-components';
import { HorizontalDivider, media } from '@higo/ui';
import { useVideoProcessQuery } from 'hooks/queries/useVideoProcessQuery';

const AuscultationGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding: 0;
  margin: 0;
  // margin-left: 35px;
  ${media.mobile.max} {
    gap: 0.5rem;
  }
`;

const Container = styled.div`
  display: flex;
  column-gap: 3rem;

  ${media.mobile.max} {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const ListContainer = styled.ul`
  flex-grow: 1;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ExamDate = styled.div`
  font-size: 12.8px;
  font-weight: 700;
  color: #72799c;
  margin-bottom: 0.5rem;
`;

type VisitsLiveNewWebsocketExamProps = {
  examId: any;
  model: VisitExaminationPageModel;
  lungsAsculationsPoints?: any;
  heartAsculationsPoints?: any;
  abdominalAsculationsPoints?: any;
};

export const VisitsLiveNewWebsocketExam = ({
  examId,
  model,
  lungsAsculationsPoints,
  heartAsculationsPoints,
  abdominalAsculationsPoints,
}: VisitsLiveNewWebsocketExamProps) => {
  const { data: examData, isLoading } = useExaminationDetailsQuery(examId);
  const patientModel = model.patient;
  const examDate = (date: Date | undefined) =>
    date && format(new Date(date), 'dd/MM/yyyy HH:mm:ss');
  const anteriorLungsPoints =
    lungsAsculationsPoints &&
    lungsAsculationsPoints.filter(
      (point: any) =>
        point.auscultationPointIndex &&
        Number(point.auscultationPointIndex) < 7,
    );
  const posteriorLungsPoints =
    lungsAsculationsPoints &&
    lungsAsculationsPoints.filter(
      (point: any) =>
        point.auscultationPointIndex &&
        Number(point.auscultationPointIndex) > 6,
    );

  const isAbdominalAuscultation =
    examData?.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.AbdominalAuscultation;

  const isHeartAuscultation =
    examData?.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.HeartAuscultation;

  const isLungsAuscultation =
    examData?.physicalExaminationDetails[0].examType ===
    PhysicalExaminationType.LungsAuscultation;
  useVideoProcessQuery(Number(examId));

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!examData) {
    return <div>Live examination error</div>;
  }

  if (isAbdominalAuscultation && abdominalAsculationsPoints) {
    return (
      <ExaminationPageLayout key={examData?.examination.id}>
        <ExaminationPageSection
          title={
            <FormattedMessage id="examinationPage.section.abdominalAuscultation.title" />
          }
          isVisitSection={true}
        >
          <ExamDate>{examDate(examData?.examination.sessionEndTime)}</ExamDate>

          <AuscultationGroupContainer>
            <AudioTrackGroupProvider>
              <Container id="asculation-group">
                <ListContainer id="asculation-list-container">
                  {abdominalAsculationsPoints.map((point: any) => {
                    return (
                      <LiveVisitAbdominalAuscultationSection
                        key={point.id}
                        point={point}
                      />
                    );
                  })}
                </ListContainer>
              </Container>
            </AudioTrackGroupProvider>
          </AuscultationGroupContainer>
          <HorizontalDivider />
        </ExaminationPageSection>
      </ExaminationPageLayout>
    );
  }

  if (isLungsAuscultation && lungsAsculationsPoints) {
    return (
      <>
        {anteriorLungsPoints && anteriorLungsPoints.length > 0 && (
          <ExaminationPageLayout key={examData?.examination.id}>
            <ExaminationPageSection
              title={
                <FormattedMessage id="examinationPage.section.lungAuscultation.title" />
              }
              isVisitSection={true}
            >
              <ExamDate>
                {examDate(examData?.examination.sessionEndTime)}
              </ExamDate>

              <AuscultationGroupContainer>
                <AudioTrackGroupProvider>
                  <Container id="asculation-group">
                    <div id="legend-container">
                      <LungsFrontAuscultationLegend
                        gender={patientModel.gender}
                        maturity={patientModel.maturity}
                      />
                    </div>
                    <ListContainer id="asculation-list-container">
                      {anteriorLungsPoints &&
                        anteriorLungsPoints.map((point: any) => {
                          return (
                            <LiveVisitLungsAsculationSection
                              key={point.examId}
                              examinationId={point.examId}
                              patientModel={model.patient}
                              point={point}
                              posteriorLungs={true}
                            />
                          );
                        })}
                    </ListContainer>
                  </Container>
                </AudioTrackGroupProvider>
              </AuscultationGroupContainer>
            </ExaminationPageSection>
          </ExaminationPageLayout>
        )}

        {posteriorLungsPoints && posteriorLungsPoints.length > 0 && (
          <ExaminationPageLayout key={examData?.examination.id}>
            <ExaminationPageSection
              title={
                <FormattedMessage id="examinationPage.section.lungAuscultation.title" />
              }
              isVisitSection={true}
            >
              <AuscultationGroupContainer>
                <AudioTrackGroupProvider>
                  <Container id="asculation-group">
                    <div id="legend-container">
                      <LungsBackAuscultationLegend
                        gender={patientModel.gender}
                        maturity={patientModel.maturity}
                      />
                    </div>

                    <ListContainer id="asculation-list-container">
                      {posteriorLungsPoints &&
                        posteriorLungsPoints.map((point: any) => {
                          return (
                            <LiveVisitLungsAsculationSection
                              key={point.examId}
                              examinationId={point.examId}
                              patientModel={model.patient}
                              point={point}
                              anteriorLungs={true}
                            />
                          );
                        })}
                    </ListContainer>
                  </Container>
                </AudioTrackGroupProvider>
              </AuscultationGroupContainer>
            </ExaminationPageSection>
          </ExaminationPageLayout>
        )}
      </>
    );
  }

  if (isHeartAuscultation && heartAsculationsPoints) {
    return (
      <ExaminationPageLayout key={examData?.examination.id}>
        <ExaminationPageSection
          title={
            <FormattedMessage id="examinationPage.section.heartAuscultation.title" />
          }
          isVisitSection={true}
        >
          <ExamDate>{examDate(examData?.examination.sessionEndTime)}</ExamDate>

          <AuscultationGroupContainer>
            <AudioTrackGroupProvider>
              <Container id="asculation-group">
                <div id="legend-container">
                  <HeartAuscultationLegend
                    gender={patientModel.gender}
                    maturity={patientModel.maturity}
                  />
                </div>
                <ListContainer id="asculation-list-container">
                  {heartAsculationsPoints.map((point: any) => {
                    return (
                      <LiveVisitHeartAusculationSection
                        examinationId={point.examId}
                        patientModel={model.patient}
                        point={point}
                      />
                    );
                  })}
                </ListContainer>
              </Container>
            </AudioTrackGroupProvider>
          </AuscultationGroupContainer>
        </ExaminationPageSection>
      </ExaminationPageLayout>
    );
  }

  return null;
};
